import React, { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  propertyApi,
  type PropertyEvaluation,
  type PropertyData,
} from '../services';

type TimePeriod = '1month' | '6months' | '12months' | '5years' | 'all';

interface PortfolioEvaluationChartProps {
  properties: PropertyData[];
  showTransactions: boolean;
  timePeriod?: TimePeriod;
  refreshTrigger?: number;
}

const PortfolioEvaluationChart: React.FC<PortfolioEvaluationChartProps> = ({
  properties,
  showTransactions,
  timePeriod = '12months',
  refreshTrigger,
}) => {
  const [evaluationsMap, setEvaluationsMap] = useState<Record<string, PropertyEvaluation[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [chartWidth, setChartWidth] = useState<number>(600);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // Update chart width based on container size
  useEffect(() => {
    const updateWidth = () => {
      if (chartWrapperRef.current) {
        const width = chartWrapperRef.current.offsetWidth;
        setChartWidth(Math.max(400, width - 20));
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    const timeoutId = setTimeout(updateWidth, 100);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timeoutId);
    };
  }, [properties.length]);

  useEffect(() => {
    const fetchAllEvaluations = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          properties.map(p => propertyApi.getEvaluations(p.id, timePeriod))
        );
        const newMap: Record<string, PropertyEvaluation[]> = {};
        properties.forEach((p, index) => {
          newMap[p.id] = results[index] || [];
        });
        setEvaluationsMap(newMap);
      } catch (error) {
        console.error('Failed to fetch evaluations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (properties.length > 0) {
      fetchAllEvaluations();
    } else {
      setEvaluationsMap({});
    }
  }, [properties, timePeriod, refreshTrigger]);

  const getEChartsOption = () => {
    if (properties.length === 0) return {};

    // 1. Collect all unique timestamps
    const timestampsSet = new Set<number>();
    
    properties.forEach(p => {
      if (p.kaufdatum) timestampsSet.add(new Date(p.kaufdatum).getTime());
      if (p.sellDate) timestampsSet.add(new Date(p.sellDate).getTime());
      
      const evals = evaluationsMap[p.id] || [];
      evals.forEach(e => {
        timestampsSet.add(new Date(e.timestamp).getTime());
      });
    });

    const sortedTimestamps = Array.from(timestampsSet).sort((a, b) => a - b);
    if (sortedTimestamps.length === 0) return {};

    const solidSeriesData: [number, number | null][] = [];
    const dashedSeriesData: [number, number | null][] = [];
    
    for (const t of sortedTimestamps) {
      let valBefore = 0;
      let valAfter = 0;

      properties.forEach(p => {
        const kaufTime = p.kaufdatum ? new Date(p.kaufdatum).getTime() : null;
        const sellTime = p.sellDate ? new Date(p.sellDate).getTime() : null;
        
        // Find closest eval < t and <= t
        let evalValBefore: number | null = null;
        let evalValAfter: number | null = null;
        const evals = evaluationsMap[p.id] || [];
        
        let closestEvalBefore: PropertyEvaluation | null = null;
        let closestEvalAfter: PropertyEvaluation | null = null;
        
        for (const e of evals) {
          const eTime = new Date(e.timestamp).getTime();
          if (eTime < t) {
            if (!closestEvalBefore || eTime > new Date(closestEvalBefore.timestamp).getTime()) {
              closestEvalBefore = e;
            }
          }
          if (eTime <= t) {
            if (!closestEvalAfter || eTime > new Date(closestEvalAfter.timestamp).getTime()) {
              closestEvalAfter = e;
            }
          }
        }
        
        if (closestEvalBefore && closestEvalBefore.vw) {
          evalValBefore = Number(closestEvalBefore.vw);
        }
        if (closestEvalAfter && closestEvalAfter.vw) {
          evalValAfter = Number(closestEvalAfter.vw);
        }

        if (showTransactions) {
          // continuousVal: The value of the property right before any instant jumps at t,
          // but INCLUDING smooth evaluation changes up to t.
          if (kaufTime !== null && kaufTime < t) {
            if (sellTime !== null && sellTime < t) {
              valBefore += ((p.sellAmount || 0) - (p.kaufpreis || 0));
            } else {
              if (evalValBefore !== null) {
                valBefore += (evalValAfter !== null ? evalValAfter : 0);
              } else {
                valBefore += 0;
              }
            }
          }
          
          // postJumpVal: The value of the property exactly at t, after all instant events (buy/sell/first eval).
          if (kaufTime !== null && kaufTime <= t) {
            if (sellTime !== null && sellTime <= t) {
              valAfter += ((p.sellAmount || 0) - (p.kaufpreis || 0));
            } else if (evalValAfter !== null) {
              valAfter += evalValAfter;
            }
          }
        } else {
          // Inactive (only gains) - NO jumps, continuous sloping
          let gain = 0;
          if (kaufTime !== null && kaufTime <= t) {
            if (sellTime !== null && sellTime <= t) {
              gain = ((p.sellAmount || 0) - (p.kaufpreis || 0));
            } else if (evalValAfter !== null) {
              gain = evalValAfter - (p.kaufpreis || 0);
            }
          }
          valBefore += gain;
          valAfter += gain;
        }
      });

      if (showTransactions && Math.abs(valAfter - valBefore) > 0.01) {
        // Jump at t!
        solidSeriesData.push([t, valBefore]);
        solidSeriesData.push([t, null]);
        solidSeriesData.push([t, valAfter]);

        dashedSeriesData.push([t, valBefore]);
        dashedSeriesData.push([t, valAfter]);
        dashedSeriesData.push([t, null]);
      } else {
        solidSeriesData.push([t, valAfter]);
        dashedSeriesData.push([t, null]);
      }
    }

    const formatCurrency = (value: number) => {
      if (value >= 1000000) {
        const millions = value / 1000000;
        return `€ ${millions.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Mio`;
      }
      return `€ ${value.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`;
    };

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(20, 20, 30, 0.9)',
        borderColor: 'rgba(167, 163, 226, 0.3)',
        textStyle: { color: '#F3F1FF' },
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#A7A3E2', type: 'dashed' },
        },
        valueFormatter: (value: number | null) => value !== null ? formatCurrency(value) : '',
      },
      grid: {
        show: true,
        borderColor: 'rgba(167, 163, 226, 0.1)',
        borderWidth: 1,
        backgroundColor: 'transparent',
        left: '4%',
        right: '4%',
        top: '10%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#A7A3E2',
          fontFamily: 'Inter, sans-serif',
          formatter: '{MMM} {yyyy}',
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#A7A3E2',
          fontFamily: 'Inter, sans-serif',
          formatter: (value: number) => formatCurrency(value),
        },
        splitLine: { show: false },
      },
      series: [
        {
          name: showTransactions ? 'Portfolio Value' : 'Portfolio Gain',
          type: 'line',
          data: solidSeriesData,
          smooth: !showTransactions, // No smooth if transactions to preserve sharp angles
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 4,
          connectNulls: false, // Don't connect across nulls (the jumps)
          itemStyle: {
            color: '#100C21',
            borderColor: '#4FFF84',
            borderWidth: 2,
          },
          lineStyle: {
            color: '#4FFF84',
            width: 3,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(79, 255, 132, 0.15)' },
                { offset: 1, color: 'rgba(79, 255, 132, 0.01)' },
              ],
            },
          },
        },
        {
          name: 'Jump',
          type: 'line',
          data: dashedSeriesData,
          smooth: false,
          showSymbol: false,
          connectNulls: false,
          lineStyle: {
            color: '#4FFF84',
            width: 2,
            type: 'dashed'
          }
        }
      ],
    };
  };

  if (loading && Object.keys(evaluationsMap).length === 0) {
    return (
      <div className="evaluation-chart-container">
        <div className="chart-loading">Loading portfolio data...</div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="evaluation-chart-container">
        <div className="chart-empty" style={{ color: '#F3F1FF' }}>
          No properties in portfolio.
        </div>
      </div>
    );
  }

  return (
    <div className="evaluation-chart-container" style={{ background: 'transparent' }}>
      <div className="chart-wrapper" ref={chartWrapperRef} style={{ width: '100%', height: '400px' }}>
        <ReactECharts
          option={getEChartsOption()}
          style={{ height: '100%', width: `${chartWidth}px` }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </div>
  );
};

export default PortfolioEvaluationChart;
